import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { api } from '@/lib/api';
import type { WikiRuntimeKind } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';

const KIND_LABEL: Record<WikiRuntimeKind, string> = {
  manifest: 'manifest.json',
  java: 'Java (PDF 변환)',
  python: 'Python (HWP 변환)',
  pdfJar: 'opendataloader-pdf jar',
  pdfHybridSidecar: 'PDF hybrid sidecar (선택 — 정확도 향상)',
  pdfImages: 'pdfimages (선택 — PDF embedded image 추출 fallback)',
  pyhwp: 'pyhwp (Python venv)',
  libreoffice: 'LibreOffice (선택)',
  tableauHyperApi: 'tableauhyperapi (Tableau hyper → DuckDB)',
  duckdbPython: 'duckdb python (Tableau hyper → DuckDB)',
};

export function AdminWikiDashboard() {
  const queryClient = useQueryClient();

  const runtimeQuery = useQuery({
    queryKey: ['admin', 'wiki', 'runtime'],
    queryFn: api.adminWikiRuntimeStatus,
  });

  const docsQuery = useQuery({
    queryKey: ['admin', 'wiki', 'documents'],
    queryFn: () => api.adminWikiDocuments(),
  });

  const pagesQuery = useQuery({
    queryKey: ['admin', 'wiki', 'pages'],
    queryFn: api.adminWikiPages,
  });

  const refreshMutation = useMutation({
    mutationFn: api.adminWikiRuntimeRefresh,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'wiki', 'runtime'], data);
    },
  });

  const docs = docsQuery.data?.documents ?? [];
  const inProgress = docs.filter(
    (d) => d.status === 'parsing' || d.status === 'digesting' || d.status === 'uploaded',
  ).length;
  const failed = docs.filter((d) => d.status === 'failed').length;
  const done = docs.filter((d) => d.status === 'done').length;
  const totalPages = pagesQuery.data?.pages.length ?? 0;

  return (
    <PageShell
      header={
        <PageHeader title="지식 위키" description="외부 변환기 런타임 상태 + 자료/페이지 카운트" />
      }
    >
      <div className="space-y-6">
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-foreground-muted">활성 페이지</div>
            <div className="mt-1 text-2xl font-semibold">{totalPages.toLocaleString()}</div>
            <Link to="/admin/wikis/pages" className="mt-2 inline-block text-xs text-accent">
              관리 →
            </Link>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-foreground-muted">자료 (전체)</div>
            <div className="mt-1 text-2xl font-semibold">{docs.length.toLocaleString()}</div>
            <div className="mt-1 text-xs text-foreground-faint">
              완료 {done} · 진행 {inProgress} · 실패 {failed}
            </div>
            <Link to="/admin/wikis/documents" className="mt-2 inline-block text-xs text-accent">
              관리 →
            </Link>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-foreground-muted">시스템 프롬프트 / 카테고리</div>
            <div className="mt-2 flex gap-3">
              <Link to="/admin/wikis/config" className="text-sm text-accent">
                프롬프트
              </Link>
              <Link to="/admin/wikis/categories" className="text-sm text-accent">
                카테고리
              </Link>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">변환기 런타임</h2>
            <button
              type="button"
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2.5 py-1 text-xs hover:border-accent disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
              재검사
            </button>
          </div>

          {runtimeQuery.isLoading ? (
            <p className="text-sm text-foreground-muted">로딩 중…</p>
          ) : runtimeQuery.isError ? (
            <p className="text-sm text-destructive">{(runtimeQuery.error as Error).message}</p>
          ) : (
            <>
              <ul className="space-y-2">
                {(runtimeQuery.data?.runtimes ?? []).map((r) => (
                  <li
                    key={r.kind}
                    className="flex items-start justify-between rounded-md border border-border bg-surface p-3"
                  >
                    <div className="flex items-start gap-2">
                      {r.ok ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
                      )}
                      <div>
                        <div className="font-medium">{KIND_LABEL[r.kind] ?? r.kind}</div>
                        {r.version ? (
                          <div className="text-xs text-foreground-muted">{r.version}</div>
                        ) : null}
                        {r.path ? (
                          <div className="text-xs text-foreground-faint break-all">{r.path}</div>
                        ) : null}
                        {r.error ? (
                          <div className="mt-1 text-xs text-destructive">{r.error}</div>
                        ) : null}
                        <div className="mt-1 text-xs text-foreground-faint">
                          필요: {r.requiredFor.join(', ')}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <details className="mt-3 text-xs text-foreground-muted">
                <summary className="cursor-pointer">설치 안내</summary>
                <pre className="mt-2 whitespace-pre-wrap rounded bg-code-bg p-3 text-xs">
                  {runtimeQuery.data?.bootstrapInstructions}
                </pre>
              </details>
            </>
          )}
        </section>
      </div>
    </PageShell>
  );
}
